import React, { useState, useEffect, useCallback } from 'react';
import {
    Table, Card, Input, Select, Button, Space, Typography,
    message, Tag, DatePicker, Row, Col
} from 'antd';
import {
    SearchOutlined, ReloadOutlined, FilterOutlined, EyeOutlined
} from '@ant-design/icons';
import { auditService } from '../services/api';
import AuditDrawer from '../components/AuditDrawer';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const PRIMARY_RED = '#DA291C';

const AuditPage = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [filterOptions, setFilterOptions] = useState({
        entities: [],
        users: [],
        action_types: []
    });

    const [filters, setFilters] = useState({
        dateRange: [dayjs().subtract(5, 'day'), dayjs()],
        user_name: null,
        action_type: null,
        entity_name: null,
        search: ''
    });

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50
    });

    const [drawer, setDrawer] = useState({
        visible: false,
        data: null
    });

    const fetchFilters = async () => {
        try {
            const res = await auditService.getFilters();
            if (res.status === 'success') {
                setFilterOptions(res.data);
            }
        } catch (error) {
            console.error('Error fetching filter options', error);
        }
    };

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const apiParams = {
                page: pagination.current,
                limit: pagination.pageSize,
                user_name: filters.user_name,
                action_type: filters.action_type,
                entity_name: filters.entity_name,
                search: filters.search,
                date_from: filters.dateRange ? filters.dateRange[0].format('YYYY-MM-DD') : null,
                date_to: filters.dateRange ? filters.dateRange[1].format('YYYY-MM-DD') : null
            };

            const response = await auditService.list(apiParams);
            if (response.status === 'success') {
                setData(response.data.items);
                setTotal(response.data.total);
            }
        } catch (error) {
            message.error('Error al cargar logs de auditoría');
        } finally {
            setLoading(false);
        }
    }, [filters, pagination.current, pagination.pageSize]);

    useEffect(() => {
        fetchFilters();
    }, []);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleTableChange = (pagination) => {
        setPagination(pagination);
    };

    const openDetail = (record) => {
        setDrawer({ visible: true, data: record });
    };

    const getActionColor = (action) => {
        switch (action) {
            case 'CREATE': return 'green';
            case 'UPDATE': return 'blue';
            case 'DELETE': return 'red';
            case 'LOGIN': return 'gold';
            case 'LOGOUT': return 'volcano';
            default: return 'default';
        }
    };

    const columns = [
        {
            title: 'Fecha',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: 180,
            render: (text) => dayjs(text).format('DD/MM/YYYY HH:mm:ss')
        },
        {
            title: 'Usuario',
            dataIndex: 'user_name',
            key: 'user_name',
            width: 150,
            render: (text) => <Typography.Text strong>{text}</Typography.Text>
        },
        {
            title: 'Acción',
            dataIndex: 'action_type',
            key: 'action_type',
            width: 120,
            render: (text) => <Tag color={getActionColor(text)}>{text}</Tag>
        },
        {
            title: 'Entidad',
            dataIndex: 'entity_name',
            key: 'entity_name',
            width: 120,
            render: (text) => text ? <Tag>{text}</Tag> : '-'
        },
        {
            title: 'Detalle',
            dataIndex: 'event_details',
            key: 'event_details',
            ellipsis: true,
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 80,
            fixed: 'right',
            render: (_, record) => (
                <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => openDetail(record)}
                />
            )
        }
    ];

    return (
        <Space direction="vertical" size="large" style={{ display: 'flex', width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={2} style={{ margin: 0 }}>Auditoría de Sistema</Title>
                <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchLogs}
                    loading={loading}
                >
                    Actualizar
                </Button>
            </div>

            <Card bodyStyle={{ padding: '16px' }}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                        <div style={{ marginBottom: 8 }}>Rango de Fechas:</div>
                        <RangePicker
                            style={{ width: '100%' }}
                            value={filters.dateRange}
                            onChange={(dates) => handleFilterChange('dateRange', dates)}
                            ranges={{
                                'Hoy': [dayjs(), dayjs()],
                                'Últimos 7 días': [dayjs().subtract(7, 'days'), dayjs()],
                                'Este mes': [dayjs().startOf('month'), dayjs().endOf('month')],
                            }}
                        />
                    </Col>
                    <Col xs={24} md={4}>
                        <div style={{ marginBottom: 8 }}>Usuario:</div>
                        <Select
                            placeholder="Todos"
                            style={{ width: '100%' }}
                            value={filters.user_name}
                            onChange={(val) => handleFilterChange('user_name', val)}
                            allowClear
                            showSearch
                        >
                            {filterOptions.users.map(u => <Option key={u} value={u}>{u}</Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} md={4}>
                        <div style={{ marginBottom: 8 }}>Acción:</div>
                        <Select
                            placeholder="Todas"
                            style={{ width: '100%' }}
                            value={filters.action_type}
                            onChange={(val) => handleFilterChange('action_type', val)}
                            allowClear
                        >
                            {filterOptions.action_types.map(a => <Option key={a} value={a}>{a}</Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} md={4}>
                        <div style={{ marginBottom: 8 }}>Entidad:</div>
                        <Select
                            placeholder="Todas"
                            style={{ width: '100%' }}
                            value={filters.entity_name}
                            onChange={(val) => handleFilterChange('entity_name', val)}
                            allowClear
                            showSearch
                        >
                            {filterOptions.entities.map(e => <Option key={e} value={e}>{e}</Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} md={4}>
                        <div style={{ marginBottom: 8 }}>Búsqueda:</div>
                        <Input
                            placeholder="Buscar en detalles..."
                            prefix={<SearchOutlined />}
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            allowClear
                        />
                    </Col>
                </Row>
            </Card>

            <Table
                columns={columns}
                dataSource={data}
                rowKey="id"
                loading={loading}
                pagination={{
                    ...pagination,
                    total: total,
                    showTotal: (total) => `Total ${total} registros`,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100']
                }}
                onChange={handleTableChange}
                scroll={{ x: 'max-content' }}
                onRow={(record) => ({
                    onDoubleClick: () => openDetail(record),
                    style: { cursor: 'pointer' }
                })}
            />

            <AuditDrawer
                visible={drawer.visible}
                data={drawer.data}
                onClose={() => setDrawer({ visible: false, data: null })}
            />
        </Space>
    );
};

export default AuditPage;
